import React, { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  ShoppingBag, ListOrdered, LogOut, Sandwich, ShieldOff,
} from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/contexts/CartContext";

const NAV = [
  { to: "/cliente", end: true, label: "Catálogo", icon: Sandwich, testid: "cli-nav-catalogo" },
  { to: "/cliente/pedidos", label: "Mis Pedidos", icon: ListOrdered, testid: "cli-nav-pedidos" },
];

export default function ClienteLayout() {
  const { user, logout, refresh } = useAuth();
  const cart = useCart();
  const navigate = useNavigate();
  const [showMenu, setShowMenu] = useState(false);

  // Refresh account status on every layout mount so a client sees suspension
  // updates without needing to log out. Cheap, one shot per page reload.
  useEffect(() => {
    refresh?.();
  }, [refresh]);

  const suspended = user?.account_status === "SUSPENDED";

  return (
    <div className="min-h-screen bg-[#F9F6F0] text-[#1A1A1A] pb-24 sm:pb-0">
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-[#E5E0D8]">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 h-20 flex items-center justify-between gap-3">
          <Link to="/cliente" className="flex items-center gap-3" data-testid="cli-brand">
            <div className="h-16 w-16 rounded-lg bg-white p-1.5 shrink-0 shadow-sm ring-1 ring-[#E5E0D8]">
              <img src="/logo.png" alt="Sandwichs Lo De Dani" className="h-full w-full object-contain" />
            </div>
            <div className="leading-tight">
              <div className="font-display font-bold text-[16px] tracking-tight">Sandwichs Lo De Dani</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#8C8C8C]">Tienda online</div>
            </div>
          </Link>

          <nav className="hidden sm:flex items-center gap-1">
            {NAV.map(({ to, end, label, icon: Icon, testid }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                data-testid={testid}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-md text-sm font-medium flex items-center gap-2 transition-colors ${
                    isActive
                      ? "bg-[#F4EAE6] text-[#C85A32]"
                      : "text-[#595959] hover:text-[#1A1A1A] hover:bg-[#F9F6F0]"
                  }`
                }
              >
                <Icon className="h-4 w-4" /> {label}
              </NavLink>
            ))}
          </nav>

          <div className="relative">
            <button
              onClick={() => setShowMenu((v) => !v)}
              className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-[#F9F6F0]"
              data-testid="cli-user-menu"
            >
              <div className="h-8 w-8 rounded-full bg-[#E9EBE4] text-[#545848] flex items-center justify-center text-[12px] font-semibold">
                {(user?.nombre || user?.email || "?").slice(0, 1).toUpperCase()}
              </div>
              <span className="hidden md:inline text-sm font-medium text-[#1A1A1A] max-w-[140px] truncate">
                {user?.nombre || user?.email}
              </span>
            </button>
            {showMenu && (
              <div
                className="absolute right-0 top-full mt-2 w-56 rounded-lg border border-[#E5E0D8] bg-white shadow-lg py-1 z-40"
                onClick={() => setShowMenu(false)}
                onMouseLeave={() => setShowMenu(false)}
                data-testid="cli-user-dropdown"
              >
                <div className="px-3 py-2 border-b border-[#E5E0D8]">
                  <div className="text-[11px] uppercase tracking-wider text-[#8C8C8C]">Sesión</div>
                  <div className="text-sm font-semibold truncate">{user?.nombre}</div>
                  <div className="text-[11px] text-[#8C8C8C] truncate">{user?.email}</div>
                </div>
                <button
                  className="w-full text-left px-3 py-2 text-sm flex items-center gap-2 text-[#8E2E2E] hover:bg-[#FBEBEB]"
                  onClick={() => { logout(); navigate("/login"); }}
                  data-testid="cli-logout"
                >
                  <LogOut className="h-4 w-4" /> Cerrar sesión
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6 sm:py-10">
        {suspended && <SuspendedBanner user={user} />}
        <Outlet context={{ suspended, suspensionReason: user?.suspension_reason }} />
      </main>

      {/* Mobile bottom nav */}
      <nav className="sm:hidden fixed bottom-0 left-0 right-0 z-30 bg-white border-t border-[#E5E0D8]">
        <div className="grid grid-cols-3">
          <NavLink
            to="/cliente"
            end
            data-testid="cli-bottom-catalogo"
            className={({ isActive }) =>
              `flex flex-col items-center gap-0.5 py-2.5 text-[11px] font-medium ${
                isActive ? "text-[#C85A32]" : "text-[#8C8C8C]"
              }`
            }
          >
            <Sandwich className="h-5 w-5" /> Catálogo
          </NavLink>
          <Link to="/cliente/carrito" className="flex flex-col items-center gap-0.5 py-2.5 text-[11px] font-medium text-[#8C8C8C] relative" data-testid="cli-bottom-carrito">
            <div className="relative">
              <ShoppingBag className="h-5 w-5" />
              {cart.count > 0 && (
                <span className="absolute -top-1.5 -right-2 min-w-[18px] h-[18px] px-1 rounded-full bg-[#C85A32] text-white text-[10px] font-bold flex items-center justify-center">
                  {cart.count}
                </span>
              )}
            </div>
            Carrito
          </Link>
          <NavLink
            to="/cliente/pedidos"
            data-testid="cli-bottom-pedidos"
            className={({ isActive }) =>
              `flex flex-col items-center gap-0.5 py-2.5 text-[11px] font-medium ${
                isActive ? "text-[#C85A32]" : "text-[#8C8C8C]"
              }`
            }
          >
            <ListOrdered className="h-5 w-5" /> Pedidos
          </NavLink>
        </div>
      </nav>

      <Toaster position="top-right" richColors />
    </div>
  );
}

function SuspendedBanner({ user }) {
  return (
    <div
      className="mb-6 rounded-2xl border border-[#E8B5B5] bg-[#FBEBEB] p-4 sm:p-5 shadow-sm"
      data-testid="cli-suspended-banner"
      role="alert"
    >
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-full bg-[#F4D6D6] text-[#8E2E2E] flex items-center justify-center shrink-0">
          <ShieldOff className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold text-[15px] sm:text-[17px] text-[#8E2E2E] leading-tight">
            Cuenta suspendida
          </div>
          <p className="text-[13px] sm:text-[14px] text-[#8E2E2E]/90 mt-1 leading-snug">
            Su cuenta está suspendida.{" "}
            <b>Hable con el vendedor para regularizar su situación.</b>{" "}
            Podés ver tu historial y remitos, pero no vas a poder enviar nuevos pedidos hasta que
            se rehabilite.
          </p>
          {user?.suspension_reason && (
            <div
              className="mt-2 inline-flex items-start gap-2 rounded-lg bg-white/60 border border-[#E8B5B5] px-3 py-2 text-[12px] text-[#8E2E2E]"
              data-testid="cli-suspended-reason"
            >
              <span className="font-semibold uppercase tracking-wider text-[10px]">Motivo</span>
              <span className="font-medium">{user.suspension_reason}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
